import {
  CarouselScreenProps,
  CarouselSection,
  ChartSection,
  LoadingAlert,
} from '@/components';
import { useYieldBearingAssetRawData } from '@/components/providers';
import {
  formatNumberToAmountInUsdString,
  formatNumberToAPYString,
  formatYieldDataForChart,
} from '@/utils/formatUtils';
import { logError, logInfo } from '@/utils/logger';
import {
  UNDERLYING_YIELD_BEARING_ASSET_DETAIL_SECTION,
  YIELD_BEARING_ASSET,
} from '@/utils/strings';
import { FC, useEffect, useState } from 'react';
interface UnderlyingYieldBearingAssetTvlAndReturnCarouselSectionProps {
  underlyingYieldBearingAsset: any;
}
const UnderlyingYieldBearingAssetTvlAndReturnCarouselSection: FC<
  UnderlyingYieldBearingAssetTvlAndReturnCarouselSectionProps
> = ({ underlyingYieldBearingAsset }) => {
  const { getAssetYieldData } = useYieldBearingAssetRawData();
  const [tvlData, setTvlData] = useState<any[]>([]);
  const [apyData, setApyData] = useState<any[]>([]);
  const [carouselScreens, setCarouselScreens] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const fetchHistoricalData = async () => {
      try {
        setLoading(true);
        logInfo(
          'Fetching historical data...',
          'UnderlyingYieldBearingAssetTvlAndReturnCarouselSection',
          {
            defiLlamaId: underlyingYieldBearingAsset.defiLlamaId,
          },
        );
        const yieldData = await getAssetYieldData(
          underlyingYieldBearingAsset.defiLlamaId,
        );
        if (yieldData?.timeSeriesData) {
          const formattedTvlData = formatYieldDataForChart(
            yieldData.timeSeriesData,
            'tvlUsd',
          );
          const formattedApyData = formatYieldDataForChart(
            yieldData.timeSeriesData,
            'apy',
          );
          setTvlData(formattedTvlData);
          setApyData(formattedApyData);
          const screens: CarouselScreenProps[] = [
            {
              mainHeader: underlyingYieldBearingAsset.apy
                ? formatNumberToAPYString(underlyingYieldBearingAsset.apy)
                : 'N/A',
              mainHeaderCaption:
                YIELD_BEARING_ASSET.CHARTS.HISTORICAL_PERFORMANCE.RETURN_TITLE,
              chipLabel:
                UNDERLYING_YIELD_BEARING_ASSET_DETAIL_SECTION.RETURN_CHART_TITLE,
              body: (
                <ChartSection
                  chartingData={formattedApyData}
                  chartId={`chart-apy-${underlyingYieldBearingAsset.id}`}
                  yAxisType="percentage"
                  noDataNode={<div>No APY data available for this asset.</div>}
                  isBackgroundTransparent={true}
                  isFullBleed={true}
                  startAtZero
                />
              ),
            },
            {
              mainHeader: underlyingYieldBearingAsset.tvlUSD
                ? formatNumberToAmountInUsdString(
                    underlyingYieldBearingAsset.tvlUSD,
                  )
                : 'N/A',
              mainHeaderCaption:
                YIELD_BEARING_ASSET.CHARTS.HISTORICAL_PERFORMANCE
                  .MARKET_CAPITALIZATION_TITLE,
              chipLabel:
                UNDERLYING_YIELD_BEARING_ASSET_DETAIL_SECTION.MARKET_CAPITALIZATION_CHART_TITLE,
              body: (
                <ChartSection
                  chartingData={formattedTvlData}
                  chartId={`chart-tvl-${underlyingYieldBearingAsset.id}`}
                  noDataNode={<div>No TVL data available for this asset.</div>}
                  isBackgroundTransparent={true}
                  isFullBleed={true}
                  startAtZero
                />
              ),
            },
          ];
          setCarouselScreens(screens);
          logInfo(
            'Historical data fetched successfully.',
            'UnderlyingYieldBearingAssetTvlAndReturnCarouselSection',
          );
        } else {
          setTvlData([]);
          setApyData([]);
          setCarouselScreens([]);
          logInfo(
            'No time series data found.',
            'UnderlyingYieldBearingAssetTvlAndReturnCarouselSection',
          );
        }
      } catch (err) {
        logError(err, 'UnderlyingYieldBearingAssetTvlAndReturnCarouselSection');
        setError('Failed to fetch historical data for this asset.');
      } finally {
        setLoading(false);
      }
    };
    fetchHistoricalData();
  }, [underlyingYieldBearingAsset, getAssetYieldData]);
  if (loading) {
    logInfo(
      'Loading data for UnderlyingYieldBearingAssetTvlAndReturnCarouselSection',
      'UnderlyingYieldBearingAssetTvlAndReturnCarouselSection',
    );
    return (
      <LoadingAlert
        title={
          UNDERLYING_YIELD_BEARING_ASSET_DETAIL_SECTION.CHART_LOADING_TITLE
        }
        description={
          UNDERLYING_YIELD_BEARING_ASSET_DETAIL_SECTION.CHART_LOADING_DESCRIPTION
        }
      />
    );
  }
  if (error) {
    logError(error, 'UnderlyingYieldBearingAssetTvlAndReturnCarouselSection');
    return null;
  }
  if (!tvlData.length && !apyData.length) {
    return null;
  }
  return <CarouselSection screens={carouselScreens} useChipNavigation={true} />;
};
export default UnderlyingYieldBearingAssetTvlAndReturnCarouselSection;
